import * as React from 'react';
import styled from 'styled-components';
import { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';

import type { RowDataType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
} from '../types';
import type { SpecialGroupType } from '../types/GroupsArrayType';
import FlexRow from '../ui/FlexRow';
import EventBox from '../EventBox';
import ShowPlus from '../components/ShowPlus';
import { ToggleEventType, IsEventSelectedType } from '../hooks/useEventSelection';
import useHoverColumns from '../hooks/useHoverColumns';

import { UNASSIGNED, AVAILABLE } from './constants';
import ScheduleCell from './ScheduleCell';

type RowWithBorderProps = {
  type: SpecialGroupType;
};
const RowWithBorder = styled(FlexRow)<RowWithBorderProps>`
  height: 100%;
  background-color: ${({ type }): string => (type === UNASSIGNED ? '#d7e6ee' : '#d7e8db')};
  border-top: 1px solid
    ${({ theme, type }): string => theme.colors[type === UNASSIGNED ? 'primary' : 'success']};
`;

const Column = styled(ScheduleCell)`
  padding-top: 8px;
`;

type Props = {
  columns: Array<string>;
  rowData: RowDataType;
  openEvent: OpenEventCallback;
  columnsCount?: number;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  shouldShowPlus: ShouldShowPlusType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
};
const SpecialGroupFirstRow: React.FC<React.PropsWithChildren<Props>> = ({
  columns,
  rowData,
  openEvent,
  columnsCount,
  shiftAppearanceMode,
  addEvent,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isShiftLineVisible,
  shouldShowPlus,
  isBusinessClosed,
  isHolidayPay,
}) => {
  const { hasNameColumn = true, events, type, group, isLast, eventHeight } = rowData;
  const { isHovered, toggleHover } = useHoverColumns(columns);

  return (
    <RowWithBorder type={type}>
      {hasNameColumn && <Column columnsCount={columnsCount} />}
      {columns.map((day) => {
        const event = events[day];
        const userForEvent = (group as any).isForDtstart ? null : { id: 0 };
        const toggle = (): void => toggleHover(day);

        const showPlus: boolean =
          isHovered(day) && shouldShowPlus({ user: userForEvent, group, day });

        return (
          <Column
            key={day}
            columnsCount={columnsCount}
            isLast={isLast}
            isBusinessClosed={isBusinessClosed({ day })}
            isHolidayPay={isHolidayPay({ day })}
            onMouseEnter={toggle}
            onMouseLeave={toggle}>
            {Boolean(event) && (
              <EventBox
                key={event.id}
                event={event}
                openEvent={openEvent}
                shiftAppearanceMode={shiftAppearanceMode}
                hoverComponent={Column}
                toggleEvent={toggleEvent}
                hasSelectedEvents={hasSelectedEvents}
                isEventSelected={isEventSelected}
                isExchange={type === AVAILABLE}
                isShiftLineVisible={isShiftLineVisible}
                height={eventHeight}
                showPlus={showPlus}
                isHovered={isHovered(day)}
              />
            )}

            {showPlus && <ShowPlus day={day} group={group} addEvent={addEvent} event={event} />}
          </Column>
        );
      })}
    </RowWithBorder>
  );
};

export default SpecialGroupFirstRow;
